import { rowsFor } from "../candidate_row_sync"
import { showToast } from "../utils/toast"
import { DueDatePicker, openDueDatePicker } from "./due_date_picker"
import { Subscription } from "@rails/actioncable"
import { subscribeCallbacksDue } from "../channels/callbacks_due_channel"

export interface FollowUpState {
  state: "active" | "stopped" | "paused"
  reason: string | null
  reason_label: string | null
  callback_due_on: string | null
  reengage_on: string | null
  toast: string | null
  undo?: Record<string, string | null>
}

const URL = "/phone_dashboard/follow_ups"
let bound = false

async function request(bpId: string, body: Record<string, unknown>): Promise<any> {
  const response = await fetch(URL, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
    },
    body: JSON.stringify({ bots_prospect_id: bpId, ...body })
  })
  const data = await response.json().catch(() => null)
  if (!response.ok || !data?.ok) throw new Error(data?.error || `HTTP ${response.status}`)
  return data
}

function swapCells(bpId: string, html: string | undefined): void {
  if (!html) return
  rowsFor(bpId).forEach(row => {
    const cell = row.querySelector(".st-auto")
    if (cell) cell.outerHTML = html
  })
}

let reloadTimer: number | undefined
let reloadSeq = 0

const PAGE_ONLY_PARAMS = ["page", "per_page", "sort", "dir", "candidate", "view", "after", "source"]

function slotUrl(slot: HTMLElement): string | null {
  const base = slot.dataset.url
  if (!base) return null
  const params = new URLSearchParams(window.location.search)
  PAGE_ONLY_PARAMS.forEach(name => params.delete(name))
  params.delete("kind")
  if (slot.dataset.kind) params.set("kind", slot.dataset.kind)
  const query = params.toString()
  return query ? `${base}?${query}` : base
}

function setBellCount(count: number): void {
  document.querySelectorAll<HTMLElement>("[data-callbacks-bell-count]").forEach(badge => {
    badge.textContent = String(count)
    badge.hidden = count <= 0
  })
}

function bellCount(): number {
  return Number(document.querySelector<HTMLElement>("[data-callbacks-bell-count]")?.textContent || 0)
}

export function reloadCallbacksDue(delay = 150): void {
  window.clearTimeout(reloadTimer)
  reloadTimer = window.setTimeout(async () => {
    const slot = document.querySelector<HTMLElement>("[data-callbacks-due-slot]")
    const url = slot && slotUrl(slot)
    if (!slot || !url) return
    const seq = ++reloadSeq
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) return
      const html = await response.text()
      if (seq !== reloadSeq) return
      slot.innerHTML = html
      slot.dataset.loaded = "1"
      setBellCount(Number(slot.querySelector<HTMLElement>("[data-callbacks-due-all]")?.dataset.callbacksDueAll || 0))
      watchLoadMore()
    } catch (_e) {
      return
    }
  }, delay)
}

function bellPanel(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-callbacks-bell-panel]")
}

function setBellOpen(open: boolean): void {
  const panel = bellPanel()
  if (!panel) return
  panel.hidden = !open
  document.querySelector<HTMLElement>("[data-callbacks-bell-toggle]")?.setAttribute("aria-expanded", String(open))
  if (!open) return
  const slot = panel.querySelector<HTMLElement>("[data-callbacks-due-slot]")
  if (slot && !slot.dataset.loaded) reloadCallbacksDue(0)
  else watchLoadMore()
}

const KEEPS_BELL_OPEN = "[data-callbacks-bell], .litepicker, .modal, .modal-backdrop, [data-continue-sending-confirm], .toast-action"

let moreObserver: IntersectionObserver | null = null

async function loadMore(sentinel: HTMLElement): Promise<void> {
  const url = sentinel.dataset.url
  if (!url || sentinel.dataset.loading) return
  sentinel.dataset.loading = "1"
  const button = sentinel.querySelector<HTMLButtonElement>("[data-callbacks-due-load]")
  if (button) {
    button.disabled = true
    button.textContent = "Loading…"
  }
  try {
    const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const html = await response.text()
    if (!sentinel.isConnected) return
    const list = sentinel.parentElement
    const template = document.createElement("template")
    template.innerHTML = html
    template.content.querySelectorAll<HTMLElement>("[data-callbacks-due-item]").forEach(item => {
      const id = item.dataset.callbacksDueItem || ""
      if (list?.querySelector(`[data-callbacks-due-item="${CSS.escape(id)}"]`)) item.remove()
    })
    sentinel.replaceWith(template.content)
    watchLoadMore()
  } catch (_e) {
    delete sentinel.dataset.loading
    if (button) {
      button.disabled = false
      button.textContent = "Load more"
    }
    showToast("Couldn't load more callbacks", "error", 3500)
  }
}

function watchLoadMore(): void {
  moreObserver?.disconnect()
  const sentinel = document.querySelector<HTMLElement>("[data-callbacks-due-more]")
  if (!sentinel || !("IntersectionObserver" in window)) return
  moreObserver = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) loadMore(sentinel)
  }, { root: sentinel.closest(".cd-list"), rootMargin: "40px" })
  moreObserver.observe(sentinel)
}

function removeDueItem(bpId: string): void {
  const items = document.querySelectorAll<HTMLElement>(`[data-callbacks-due-item="${CSS.escape(bpId)}"]`)
  if (!items.length) return
  items.forEach(item => item.remove())
  setBellCount(Math.max(bellCount() - 1, 0))
  document.querySelectorAll<HTMLElement>("[data-callbacks-due] [data-callbacks-due-total]").forEach(badge => {
    badge.textContent = String(Math.max(Number(badge.textContent || 0) - 1, 0))
  })
}

export async function resumeFollowUps(bpId: string): Promise<void> {
  try {
    const data = await request(bpId, { op: "resume" })
    swapCells(bpId, data.cell)
    removeDueItem(bpId)
    reloadCallbacksDue()
    showToast("Follow-ups resumed")
  } catch (_e) {
    showToast("Couldn't resume follow-ups", "error", 3500)
  }
}

async function reschedule(bpId: string, date: string): Promise<void> {
  try {
    const data = await request(bpId, { op: "reschedule", date })
    swapCells(bpId, data.cell)
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
    if (!date || date > today) removeDueItem(bpId)
    reloadCallbacksDue()
    showToast(data.follow_ups?.toast || (date ? "Date saved" : "Reminder cleared"))
  } catch (_e) {
    showToast("Couldn't save the date", "error", 3500)
  }
}

function syncPhoneStatus(bpId: string, status: string, html: string | undefined): void {
  rowsFor(bpId).forEach(row => {
    row.dataset.phone = status
    const cell = row.querySelector(".st-phone")
    if (cell && html) cell.outerHTML = html
  })
  document.querySelectorAll<HTMLSelectElement>(`select.status_calling[data-bots-prospect-id="${CSS.escape(bpId)}"]`).forEach(select => {
    select.value = status
    select.className = `form-control status_calling ${status}`.trim()
  })
}

async function undoFollowUps(bpId: string, snapshot: Record<string, string | null>): Promise<void> {
  try {
    const data = await request(bpId, { op: "undo", undo: snapshot })
    swapCells(bpId, data.cell)
    syncPhoneStatus(bpId, data.status_calling || "", data.phone_cell)
    reloadCallbacksDue()
    showToast("Phone status change undone")
  } catch (_e) {
    showToast("Couldn't undo the change", "error", 3500)
  }
}

export function announceFollowUps(bpId: string, followUps: FollowUpState | null | undefined): void {
  reloadCallbacksDue()
  if (!followUps?.toast) return
  const snapshot = followUps.undo
  const undo = snapshot ? () => undoFollowUps(bpId, snapshot) : () => resumeFollowUps(bpId)
  showToast(followUps.toast, "success", 6000, { label: "Undo", onClick: undo })
}

let calendar: DueDatePicker | null = null

function openDatePicker(chip: HTMLElement): void {
  const input = chip.parentElement?.querySelector<HTMLInputElement>("[data-follow-ups-date]")
  const bpId = chip.closest<HTMLElement>("[data-bp-id]")?.dataset.bpId
  if (!input || !bpId) return

  const wasOpen = calendar?.isOpen()
  calendar?.hide()
  if (wasOpen) return

  calendar = openDueDatePicker(input, chip, date => reschedule(bpId, date), {
    weekdaysOnly: true,
    dropdowns: true,
    className: "lp-refresh"
  })
}

let pendingResume: string | null = null
let pendingWrite: { bpId: string; prospectId: string } | null = null

async function markWritten(bpId: string): Promise<void> {
  try {
    const data = await request(bpId, { op: "reschedule", date: "" })
    swapCells(bpId, data.cell)
    removeDueItem(bpId)
    reloadCallbacksDue()
    showToast("Email sent · removed from Callbacks due")
  } catch (_e) {
    showToast("Email sent, but couldn't clear the reminder. Click Done.", "error", 5000)
  }
}

function confirmBox(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-continue-sending-confirm]")
}

function openConfirm(bpId: string, holder: HTMLElement | null): void {
  const box = confirmBox()
  if (!box) {
    resumeFollowUps(bpId)
    return
  }
  pendingResume = bpId
  const name = holder?.dataset.candidateName || "this candidate"
  const campaign = holder?.dataset.campaignName || ""
  const nameEl = box.querySelector<HTMLElement>("[data-continue-sending-name]")
  const campaignEl = box.querySelector<HTMLElement>("[data-continue-sending-campaign]")
  const campaignWrap = box.querySelector<HTMLElement>("[data-continue-sending-campaign-wrap]")
  if (nameEl) nameEl.textContent = name
  if (campaignEl) campaignEl.textContent = campaign
  if (campaignWrap) campaignWrap.hidden = !campaign
  box.classList.add("open")
  box.querySelector<HTMLElement>("[data-continue-sending-accept]")?.focus()
}

function closeConfirm(): void {
  pendingResume = null
  confirmBox()?.classList.remove("open")
}

export function bindFollowUps(): void {
  watchLoadMore()
  if (bound) return
  bound = true

  document.addEventListener("click", event => {
    const target = event.target as HTMLElement | null
    if (!target) return

    if (target.closest("[data-callbacks-bell-toggle]")) {
      setBellOpen(!!bellPanel()?.hidden)
      return
    }
    if (bellPanel() && !bellPanel()!.hidden && target.isConnected && !target.closest(KEEPS_BELL_OPEN)) setBellOpen(false)

    if (target.closest("[data-continue-sending-accept]")) {
      const bpId = pendingResume
      closeConfirm()
      if (bpId) resumeFollowUps(bpId)
      return
    }
    if (target.closest("[data-continue-sending-cancel]") || target.matches("[data-continue-sending-confirm]")) {
      closeConfirm()
      return
    }

    const rescheduleChip = target.closest<HTMLElement>("[data-follow-ups-reschedule]")
    if (rescheduleChip) {
      openDatePicker(rescheduleChip)
      return
    }

    const resume = target.closest<HTMLElement>("[data-follow-ups-resume]")
    if (resume) {
      const holder = resume.closest<HTMLElement>("[data-bp-id]")
      if (holder?.dataset.bpId) openConfirm(holder.dataset.bpId, holder)
      return
    }

    const write = target.closest<HTMLElement>("[data-callbacks-due-write]")
    if (write) {
      const bpId = write.closest<HTMLElement>("[data-bp-id]")?.dataset.bpId
      pendingWrite = bpId ? { bpId, prospectId: write.dataset.prospectId || "" } : null
      return
    }

    const kindButton = target.closest<HTMLElement>("[data-callbacks-due-kind]")
    if (kindButton) {
      const slot = kindButton.closest<HTMLElement>("[data-callbacks-due-slot]")
      if (slot) {
        slot.dataset.kind = kindButton.dataset.callbacksDueKind || ""
        slot.querySelectorAll<HTMLElement>("[data-callbacks-due-kind]").forEach(button => {
          const active = button === kindButton
          button.classList.toggle("is-active", active)
          button.setAttribute("aria-pressed", String(active))
        })
        reloadCallbacksDue(0)
      }
      return
    }

    const more = target.closest<HTMLElement>("[data-callbacks-due-more]")
    if (more && target.closest("[data-callbacks-due-load]")) {
      loadMore(more)
      return
    }

    const done = target.closest<HTMLElement>("[data-callbacks-due-done]")
    if (done) {
      const bpId = done.closest<HTMLElement>("[data-bp-id]")?.dataset.bpId
      if (bpId) reschedule(bpId, "")
    }
  })

  document.addEventListener("email-composer:sent", event => {
    const prospectId = (event as CustomEvent<{ prospectId: string }>).detail?.prospectId
    const pending = pendingWrite
    pendingWrite = null
    if (pending && prospectId && pending.prospectId === prospectId) markWritten(pending.bpId)
  })

  document.addEventListener("keydown", event => {
    if (event.key !== "Escape") return
    if (confirmBox()?.classList.contains("open")) closeConfirm()
    else if (bellPanel() && !bellPanel()!.hidden && !document.querySelector(".modal.show")) setBellOpen(false)
  })

}

let cable: Subscription | null = null

function syncCable(present: boolean): void {
  if (present && !cable) cable = subscribeCallbacksDue(() => reloadCallbacksDue(300))
  if (!present && cable) {
    cable.unsubscribe()
    cable = null
  }
}

function bindWhenBellPresent(): void {
  const present = !!document.querySelector("[data-callbacks-bell]")
  if (present) bindFollowUps()
  syncCable(present)
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && document.querySelector("[data-callbacks-bell]")) reloadCallbacksDue()
})

document.addEventListener("turbo:load", bindWhenBellPresent)
document.addEventListener("DOMContentLoaded", bindWhenBellPresent)
